/* ==========================================================================
   Generate Checkout Link Page
   Task F33: Internal tool for TJM sales reps to generate Stripe checkout URLs
   ========================================================================== */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Copy, Check, ExternalLink } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Alert } from '@/components/ui/Alert'
import {
  generateCheckoutLink,
  type SubscriptionTier,
  type BillingCycle,
  type Currency,
} from '@/api/internal'
import { ApiClientError } from '@/api/client'

// Prices in dollars; must match backend TIER_CONFIG in app/billing/tiers.py.
const TIERS: {
  value: SubscriptionTier
  name: string
  monthlyPrice: number
  annualPrice: number
}[] = [
  { value: 'starter', name: 'Starter', monthlyPrice: 299, annualPrice: 3000 },
  { value: 'growth', name: 'Growth', monthlyPrice: 799, annualPrice: 8000 },
  { value: 'scale', name: 'Scale', monthlyPrice: 1499, annualPrice: 15000 },
]

// CAD prices match the USD amounts, so only the symbol changes.
const CURRENCIES: { value: Currency; label: string; symbol: string }[] = [
  { value: 'usd', label: 'USD ($)', symbol: '$' },
  { value: 'cad', label: 'CAD (CA$)', symbol: 'CA$' },
]

export function GenerateCheckoutLink() {
  const navigate = useNavigate()

  const [customerEmail, setCustomerEmail] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [currency, setCurrency] = useState<Currency>('usd')
  const [tier, setTier] = useState<SubscriptionTier>('growth')
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('annual')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [checkoutUrl, setCheckoutUrl] = useState<string>()
  const [copied, setCopied] = useState(false)

  const currencySymbol =
    CURRENCIES.find((c) => c.value === currency)?.symbol ?? '$'
  const isAnnual = billingCycle === 'annual'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!customerEmail.trim() || !customerName.trim()) {
      setError('Customer email and name are required')
      return
    }

    setLoading(true)
    setError(undefined)
    setCheckoutUrl(undefined)

    try {
      const result = await generateCheckoutLink({
        customer_email: customerEmail.trim().toLowerCase(),
        customer_name: customerName.trim(),
        tier,
        billing_cycle: billingCycle,
        currency,
      })
      setCheckoutUrl(result.checkout_url)
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          navigate('/internal/login', { replace: true })
          return
        }
        setError(err.getUserMessage())
      } else {
        setError('An unexpected error occurred. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    if (!checkoutUrl) return
    try {
      await navigator.clipboard.writeText(checkoutUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = checkoutUrl
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleReset = () => {
    setCheckoutUrl(undefined)
    setCustomerEmail('')
    setCustomerName('')
    setError(undefined)
    // Currency resets but tier/cycle stay: CAD amounts match USD, so a stale
    // CAD selection is invisible in the price and would silently bill the
    // next customer in the wrong currency.
    setCurrency('usd')
  }

  return (
      <div className="py-12">
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-serif text-2xl sm:text-3xl text-charcoal mb-2 text-center">
            Generate Checkout Link
          </h1>
          <p className="text-center text-charcoal/70 mb-8">
            Create a Stripe Checkout URL to send to a customer.
          </p>

          <Card className="p-6 sm:p-8">
            {error && (
              <Alert variant="error" className="mb-6">
                {error}
              </Alert>
            )}

            {checkoutUrl ? (
              <div>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
                    <Check className="h-6 w-6 text-green-600" aria-hidden="true" />
                  </div>
                  <h2 className="font-semibold text-charcoal">Link generated</h2>
                </div>

                <div className="bg-charcoal/5 rounded-lg p-4 mb-4">
                  <p className="text-xs text-charcoal/50 mb-1 font-medium">Checkout URL</p>
                  <p className="text-sm text-charcoal break-all font-mono">
                    {checkoutUrl}
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="primary"
                    size="md"
                    className="flex-1"
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-2" aria-hidden="true" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-2" aria-hidden="true" />
                        Copy Link
                      </>
                    )}
                  </Button>
                  <a
                    href={checkoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="secondary" size="md">
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </a>
                </div>

                <button
                  onClick={handleReset}
                  className="w-full mt-4 text-sm text-deep-purple hover:underline"
                >
                  Generate another link
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="space-y-5">
                  <FormField label="Customer Name" required htmlFor="customer-name">
                    <Input
                      id="customer-name"
                      type="text"
                      placeholder="Acme Compounding Pharmacy"
                      value={customerName}
                      onChange={(e) => { setCustomerName(e.target.value); setError(undefined) }}
                      disabled={loading}
                    />
                  </FormField>

                  <FormField label="Customer Email" required htmlFor="customer-email">
                    <Input
                      id="customer-email"
                      type="email"
                      placeholder="owner@acmepharmacy.com"
                      value={customerEmail}
                      onChange={(e) => { setCustomerEmail(e.target.value); setError(undefined) }}
                      disabled={loading}
                    />
                  </FormField>

                  <FormField label="Currency" required htmlFor="currency-select">
                    <select
                      id="currency-select"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value as Currency)}
                      disabled={loading}
                      className="w-full rounded-md border border-charcoal/20 bg-white px-3 py-2.5 text-sm text-charcoal focus:border-deep-purple focus:outline-none focus:ring-2 focus:ring-lavender"
                    >
                      {CURRENCIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </FormField>

                  <FormField label="Tier" required htmlFor="tier-select">
                    <select
                      id="tier-select"
                      value={tier}
                      onChange={(e) => setTier(e.target.value as SubscriptionTier)}
                      disabled={loading}
                      className="w-full rounded-md border border-charcoal/20 bg-white px-3 py-2.5 text-sm text-charcoal focus:border-deep-purple focus:outline-none focus:ring-2 focus:ring-lavender"
                    >
                      {TIERS.map((t) => (
                        <option key={t.value} value={t.value}>
                          {`${t.name} (${currencySymbol}${(isAnnual ? t.annualPrice : t.monthlyPrice).toLocaleString('en-US')}${isAnnual ? '/yr' : '/mo'})`}
                        </option>
                      ))}
                    </select>
                  </FormField>

                  <FormField label="Billing Cycle" required htmlFor="billing-cycle">
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="billing-cycle"
                          value="monthly"
                          checked={billingCycle === 'monthly'}
                          onChange={() => setBillingCycle('monthly')}
                          disabled={loading}
                          className="accent-deep-purple"
                        />
                        <span className="text-sm text-charcoal">Monthly</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="billing-cycle"
                          value="annual"
                          checked={billingCycle === 'annual'}
                          onChange={() => setBillingCycle('annual')}
                          disabled={loading}
                          className="accent-deep-purple"
                        />
                        <span className="text-sm text-charcoal">Annual</span>
                      </label>
                    </div>
                  </FormField>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={loading}
                  disabled={loading}
                  className="w-full mt-6"
                >
                  Generate Link
                </Button>
              </form>
            )}
          </Card>
        </div>
      </div>
  )
}

export default GenerateCheckoutLink
